/**
 * apps/koordinator-app/src/shared/contexts/index.ts
 * 
 * Koordinator-specific auth compatibility layer.
 * 
 * ============================================================================
 * WHY KOORDINATOR IS DIFFERENT
 * ============================================================================
 * 
 * Most apps (Kloutforce, Katalog, Admin) use the `app_user_map` table to link
 * a Supabase auth user to an app-specific profile. The shared @bright-web-skeleton/auth
 * package handles this via `getAppUserIdForAuthUser()` and `getAppProfile()`.
 * 
 * **Kloutsale is different**: Brand users are linked directly to the `brands`
 * table via `brands.user_id` (a foreign key to auth.users). There is no entry
 * in `app_user_map` for Kloutsale brand owners.
 * 
 * Therefore:
 * - `onboarded` for Kloutsale means "a brand record exists for this user"
 * - We query `brands` directly instead of relying on `appProfile !== null`
 * - `refreshOnboardedStatus()` re-checks the `brands` table
 * 
 * DO NOT "FIX" THIS by switching back to `appProfile !== null`. That would
 * break onboarding because Kloutsale doesn't use `app_user_map`.
 * 
 * ============================================================================
 * AUTH INVARIANT (MONOREPO-WIDE)
 * ============================================================================
 * - authReady is the ONLY signal effects may depend on
 * - user/session MUST NOT be used as effect dependencies
 * - All data fetching must use useRef guards for idempotency
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth as useSharedAuth, useAuthReady as useSharedAuthReady, warnIfNotReady } from '@bright-web-skeleton/auth';
import { supabase } from '@bright-web-skeleton/supabase';

// Re-export AuthProvider directly from shared package
export { AuthProvider } from '@bright-web-skeleton/auth';

// Re-export auth lifecycle utilities
export { warnIfNotReady };

/**
 * Kloutsale-specific User interface for backwards compatibility.
 */
export interface User {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  role?: string;
  /** 
   * KOORDINATOR-SPECIFIC: True if user has a brand in the `brands` table.
   * This is checked directly, NOT via app_user_map.
   */
  onboarded?: boolean;
  user_metadata?: {
    full_name?: string;
    name?: string;
    avatar_url?: string;
    [key: string]: unknown;
  };
}

/**
 * Kloutsale-specific AuthContextType for backwards compatibility.
 */
export interface AuthContextType {
  user: User | null;
  session: unknown | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-checks the brands table to update onboarded status */
  refreshOnboardedStatus: () => Promise<void>;
  /** True while auth OR brand check is in progress */
  loading: boolean;
  /** STABLE SIGNAL: True exactly once after auth + brand check completes */
  authReady: boolean;
  authError: Error | null;
  isSessionExpired: boolean;
  clearAuthError: () => void;
}

/**
 * useAuth hook for Kloutsale app.
 * 
 * KOORDINATOR-SPECIFIC: Checks `brands` table directly for onboarded status
 * instead of relying on `app_user_map` / `appProfile`.
 */
export function useAuth(): AuthContextType {
  const sharedAuth = useSharedAuth();
  
  // Brand check state
  // null = not yet checked, true = has brand, false = no brand
  const [hasBrand, setHasBrand] = useState<boolean | null>(null);
  const [brandCheckComplete, setBrandCheckComplete] = useState(false);
  const brandCheckRef = useRef(false);

  /**
   * Check if user has a brand (determines onboarded status).
   * Runs once after authReady, uses useRef guard for idempotency.
   */
  useEffect(() => {
    // Wait for shared auth to be ready
    if (!sharedAuth.authReady) return;
    
    // Prevent duplicate execution (strict mode, HMR, etc.)
    if (brandCheckRef.current) return;
    
    const userId = sharedAuth.authUser?.id;
    
    // No user = no brand check needed
    if (!userId) {
      setHasBrand(null);
      setBrandCheckComplete(true);
      return;
    }

    brandCheckRef.current = true;
    
    const checkBrand = async () => {
      try {
        // Query brands table - respects RLS policies
        const { data, error } = await supabase
          .from('brands')
          .select('id')
          .eq('user_id', userId)
          .limit(1)
          .maybeSingle();
        
        if (error) {
          // Log but don't crash - treat as no brand
          console.warn('[useAuth:koordinator] Brand check error:', error.message);
          setHasBrand(false);
        } else {
          setHasBrand(!!data);
        }
      } catch (err) {
        console.error('[useAuth:koordinator] Brand check exception:', err);
        setHasBrand(false);
      } finally {
        setBrandCheckComplete(true);
      }
    };
    
    checkBrand();
  }, [sharedAuth.authReady]); // eslint-disable-line react-hooks/exhaustive-deps -- AUTH INVARIANT

  /**
   * Refresh onboarded status by re-checking brands table.
   * Called after brand creation in onboarding flow.
   */
  const refreshOnboardedStatus = useCallback(async () => {
    const userId = sharedAuth.authUser?.id;
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('brands')
        .select('id')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.warn('[useAuth:koordinator] Refresh brand check error:', error.message);
        return;
      }
      
      setHasBrand(!!data);
    } catch (err) {
      console.error('[useAuth:koordinator] Refresh brand check exception:', err);
    }
  }, [sharedAuth.authUser?.id]);

  // Kloutsale-specific authReady: shared auth ready AND brand check complete
  const koordinatorAuthReady = sharedAuth.authReady && brandCheckComplete;
  
  // Loading: shared auth loading OR brand check in progress (for logged-in users)
  const isLoading = sharedAuth.loading || (sharedAuth.authReady && !!sharedAuth.authUser && !brandCheckComplete);

  // Map shared user to koordinator User interface
  const user: User | null = sharedAuth.authUser ? {
    id: sharedAuth.authUser.id,
    email: sharedAuth.authUser.email || '',
    name: sharedAuth.authUser.user_metadata?.full_name as string || 
          sharedAuth.authUser.user_metadata?.name as string || 
          sharedAuth.authUser.email?.split('@')[0] || '',
    avatar_url: sharedAuth.authUser.user_metadata?.avatar_url as string ||
                sharedAuth.authUser.user_metadata?.picture as string,
    role: 'brand',
    // KOORDINATOR: onboarded = has a brand in the brands table
    onboarded: hasBrand === true,
    user_metadata: sharedAuth.authUser.user_metadata as User['user_metadata'],
  } : null;

  return {
    user,
    session: null, // Session is internal to shared auth
    signIn: sharedAuth.login,
    signUp: async (email: string, password: string, name?: string) => {
      await sharedAuth.signup(email, password, name);
    },
    signInWithGoogle: async () => {
      await sharedAuth.loginWithGoogle('/auth/callback');
    },
    signOut: sharedAuth.logout,
    refreshOnboardedStatus,
    loading: isLoading,
    authReady: koordinatorAuthReady,
    authError: null,
    isSessionExpired: sharedAuth.isSessionExpired,
    clearAuthError: () => {},
  };
}

/**
 * Hook that returns ONLY the authReady signal.
 * Use this for gating effects - encourages correct usage.
 */
export function useAuthReady(): { authReady: boolean } {
  return useSharedAuthReady();
}
